import { Coins, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { StoryImportCreditQuote, StoryImportDraft } from "@/features/story-import/story-import.types";

interface StoryImportCreditStepProps {
  draft: StoryImportDraft | null;
  isStarting: boolean;
  quote: StoryImportCreditQuote | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function StoryImportCreditStep({
  draft,
  isStarting,
  quote,
  onCancel,
  onConfirm,
}: StoryImportCreditStepProps) {
  const { t } = useTranslation();
  const formattedEstimatedWordCount =
    typeof quote?.estimatedWordCount === "number" && quote.estimatedWordCount > 0
      ? new Intl.NumberFormat().format(quote.estimatedWordCount)
      : "—";
  const formattedEstimatedTokenCount =
    typeof quote?.estimatedTokenCount === "number" && quote.estimatedTokenCount > 0
      ? new Intl.NumberFormat().format(quote.estimatedTokenCount)
      : null;
  const formattedEstimatedAnalysisTokenCount =
    typeof quote?.estimatedAnalysisTokenCount === "number" && quote.estimatedAnalysisTokenCount > 0
      ? new Intl.NumberFormat().format(quote.estimatedAnalysisTokenCount)
      : null;

  return (
    <Card>
      <CardContent className="space-y-6 p-8">
        <div className="space-y-2">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-clay">
            {t("storyImport.creditEstimate.eyebrow")}
          </p>
          <h2 className="text-3xl text-foreground">{t("storyImport.creditEstimate.title")}</h2>
          <p className="max-w-3xl text-base leading-7 text-muted-foreground">
            {t("storyImport.creditEstimate.body")}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-[1.5rem] border border-ink/10 bg-white/70 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-clay">
              <Coins className="h-4 w-4" />
              {t("storyImport.creditEstimate.creditCost")}
            </div>
            <p className="mt-3 text-3xl text-foreground">{quote?.creditCost ?? 0}</p>
          </div>

          <div className="rounded-[1.5rem] border border-ink/10 bg-white/70 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-clay">
              <Shield className="h-4 w-4" />
              {t("storyImport.creditEstimate.currentBalance")}
            </div>
            <p className="mt-3 text-3xl text-foreground">{quote?.currentBalance ?? 0}</p>
          </div>

          <div className="rounded-[1.5rem] border border-ink/10 bg-white/70 p-5">
            <p className="text-sm font-semibold text-clay">
              {t("storyImport.creditEstimate.accountTier")}
            </p>
            <p className="mt-3 text-3xl capitalize text-foreground">
              {quote?.tier ? t(`common.tiers.${quote.tier}`) : t("common.tiers.free")}
            </p>
          </div>

          <div className="rounded-[1.5rem] border border-ink/10 bg-white/70 p-5">
            <p className="text-sm font-semibold text-clay">
              {t("storyImport.creditEstimate.estimatedLength")}
            </p>
            <p className="mt-3 text-3xl text-foreground">{formattedEstimatedWordCount}</p>
            {formattedEstimatedTokenCount ? (
              <p className="mt-2 text-sm text-muted-foreground">
                {t("storyImport.creditEstimate.estimatedTokens", {
                  count: formattedEstimatedTokenCount,
                })}
              </p>
            ) : null}
            {formattedEstimatedAnalysisTokenCount ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {t("storyImport.creditEstimate.estimatedAnalysisTokens", {
                  count: formattedEstimatedAnalysisTokenCount,
                })}
              </p>
            ) : null}
          </div>
        </div>

        {draft ? (
          <div className="rounded-[1.5rem] border border-ink/10 bg-white/65 px-5 py-4 text-sm text-muted-foreground">
            {t("storyImport.creditEstimate.stagedFile", {
              fileName: draft.fileName,
            })}
          </div>
        ) : null}

        <div className="rounded-[1.5rem] border border-ink/10 bg-canvas/75 px-5 py-4">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-clay">
            {t("storyImport.creditEstimate.faithfulTitle")}
          </p>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
            {t("storyImport.creditEstimate.faithfulBody")}
          </p>
        </div>

        <div className="flex flex-wrap justify-between gap-3">
          <Button onClick={onCancel} type="button" variant="outline">
            {t("storyImport.actions.cancelImport")}
          </Button>
          <Button
            disabled={isStarting || quote?.allowed === false}
            onClick={onConfirm}
            type="button"
          >
            {isStarting
              ? t("storyImport.actions.starting")
              : t("storyImport.actions.startProcessing")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
